/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import type {
  AdvertorialConfig,
  DiscoveryConfig,
  DiscoveryTiming,
  ExtractionConfig,
  ExtractionType,
  PlacementIdConfig,
  PlacementIdStrategy,
  TransformType,
} from '../../types/advertorial';

/**
 * Configuration validation error
 */
export class ConfigValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfigValidationError';
  }
}

/**
 * AdvertorialConfig class with validation
 */
export class AdvertorialConfigValidator {
  private readonly config: AdvertorialConfig;

  constructor(config: AdvertorialConfig) {
    this.config = config;
    this.validate();
  }

  /**
   * Get the validated configuration
   */
  public getConfig(): AdvertorialConfig {
    return this.config;
  }

  /**
   * Get discovery configuration
   */
  public getDiscoveryConfig(): DiscoveryConfig {
    return this.config.discovery;
  }

  /**
   * Get placement ID configuration
   */
  public getPlacementIdConfig(): PlacementIdConfig {
    return this.config.placement_id;
  }

  /**
   * Get extraction configuration
   */
  public getExtractionConfig(): ExtractionConfig {
    return this.config.extraction;
  }

  /**
   * Validate the entire configuration
   */
  private validate(): void {
    this.validateDiscovery();
    this.validatePlacementId();
    this.validateExtraction();
  }

  /**
   * Validate discovery configuration
   */
  private validateDiscovery(): void {
    if (!this.config.discovery) {
      throw new ConfigValidationError('discovery configuration is required');
    }

    const { timing, selectors, delay } = this.config.discovery;

    const validTimings: DiscoveryTiming[] = [
      'immediate',
      'dom-ready',
      'load',
      'delayed',
      'observe',
      'custom-event',
    ];
    if (!validTimings.includes(timing)) {
      throw new ConfigValidationError(
        `discovery.timing must be one of: ${validTimings.join(', ')}`,
      );
    }

    if (timing === 'custom-event') {
      // Selectors are optional for custom-event timing, but if provided must be valid
      if (selectors !== undefined && !Array.isArray(selectors)) {
        throw new ConfigValidationError('discovery.selectors must be an array if provided');
      }
      if (Array.isArray(selectors)) {
        for (const selector of selectors) {
          if (typeof selector !== 'string' || selector.trim() === '') {
            throw new ConfigValidationError('discovery.selectors must contain valid CSS selectors');
          }
        }
      }
    } else {
      if (!Array.isArray(selectors) || selectors.length === 0) {
        throw new ConfigValidationError('discovery.selectors must be a non-empty array');
      }

      for (const selector of selectors) {
        if (typeof selector !== 'string' || selector.trim() === '') {
          throw new ConfigValidationError('discovery.selectors must contain valid CSS selectors');
        }
      }
    }

    // Validate delay if timing is 'delayed' (required) or 'observe' (optional)
    if (timing === 'delayed') {
      if (typeof delay !== 'number' || delay < 0) {
        throw new ConfigValidationError(
          'discovery.delay must be a non-negative number when timing is "delayed"',
        );
      }
    } else if (timing === 'observe' && delay !== undefined) {
      if (typeof delay !== 'number' || delay < 0) {
        throw new ConfigValidationError(
          'discovery.delay must be a non-negative number when provided for "observe" timing',
        );
      }
    }

    if (timing === 'custom-event') {
      this.validateEventDiscoveryConfig();
    }
  }

  /**
   * Validate event discovery configuration (for 'custom-event' timing)
   */
  private validateEventDiscoveryConfig(): void {
    const { event } = this.config.discovery;

    if (!event) {
      throw new ConfigValidationError('discovery.event is required when timing is "custom-event"');
    }

    if (typeof event !== 'object' || Array.isArray(event)) {
      throw new ConfigValidationError('discovery.event must be an object');
    }

    // Validate event.type: must be non-empty and trimmed
    if (typeof event.type !== 'string' || event.type.trim() === '') {
      throw new ConfigValidationError(
        'discovery.event.type is required and must be a non-empty string',
      );
    }
    if (event.type !== event.type.trim()) {
      throw new ConfigValidationError(
        'discovery.event.type must not have leading or trailing whitespace',
      );
    }

    // Validate event.dpath: must be non-empty and trimmed
    if (typeof event.dpath !== 'string' || event.dpath.trim() === '') {
      throw new ConfigValidationError(
        'discovery.event.dpath is required and must be a non-empty string',
      );
    }
    if (event.dpath !== event.dpath.trim()) {
      throw new ConfigValidationError(
        'discovery.event.dpath must not have leading or trailing whitespace',
      );
    }

    // Validate event.attribute: must be non-empty, trimmed, and a valid attribute name
    if (typeof event.attribute !== 'string' || event.attribute.trim() === '') {
      throw new ConfigValidationError(
        'discovery.event.attribute is required and must be a non-empty string',
      );
    }
    if (event.attribute !== event.attribute.trim()) {
      throw new ConfigValidationError(
        'discovery.event.attribute must not have leading or trailing whitespace',
      );
    }
    // Validate attribute name pattern: must start with letter/underscore, followed by word chars, colons, or hyphens
    // This prevents selector injection and matches valid HTML attribute names
    if (!/^[a-zA-Z_][\w:-]*$/.test(event.attribute)) {
      throw new ConfigValidationError(
        'discovery.event.attribute must be a valid attribute name (start with letter or underscore, contain only letters, digits, underscores, hyphens, or colons)',
      );
    }
  }

  private validatePlacementId(): void {
    if (!this.config.placement_id) {
      throw new ConfigValidationError('placement_id configuration is required');
    }

    const { strategy, attribute, prefix, fallback } = this.config.placement_id;

    const validStrategies: PlacementIdStrategy[] = [
      'data-attribute',
      'css-path',
      'position',
      'container-id',
    ];
    if (!validStrategies.includes(strategy)) {
      throw new ConfigValidationError(
        `placement_id.strategy must be one of: ${validStrategies.join(', ')}`,
      );
    }

    // Validate attribute if strategy is 'data-attribute'
    if (strategy === 'data-attribute') {
      if (typeof attribute !== 'string' || attribute.trim() === '') {
        throw new ConfigValidationError(
          'placement_id.attribute is required when strategy is "data-attribute"',
        );
      }
    }

    if (prefix !== undefined && (typeof prefix !== 'string' || prefix.trim() === '')) {
      throw new ConfigValidationError('placement_id.prefix must be a non-empty string if provided');
    }

    if (fallback !== undefined && !validStrategies.includes(fallback)) {
      throw new ConfigValidationError(
        `placement_id.fallback must be one of: ${validStrategies.join(', ')}`,
      );
    }
  }

  /**
   * Validate extraction configuration
   */
  private validateExtraction(): void {
    if (!this.config.extraction) {
      throw new ConfigValidationError('extraction configuration is required');
    }

    if (typeof this.config.extraction !== 'object' || Array.isArray(this.config.extraction)) {
      throw new ConfigValidationError('extraction must be an object');
    }

    const fieldNames = Object.keys(this.config.extraction);
    if (fieldNames.length === 0) {
      throw new ConfigValidationError('extraction must contain at least one field');
    }

    for (const fieldName of fieldNames) {
      const rule = this.config.extraction[fieldName];
      this.validateExtractionRule(fieldName, rule);
    }
  }

  /**
   * Validate a single extraction rule
   */
  private validateExtractionRule(fieldName: string, rule: any): void {
    if (!rule || typeof rule !== 'object') {
      throw new ConfigValidationError(`extraction.${fieldName} must be an object`);
    }

    const validTypes: ExtractionType[] = ['url-parameter', 'attribute', 'element-text', 'regex'];
    if (!validTypes.includes(rule.type)) {
      throw new ConfigValidationError(
        `extraction.${fieldName}.type must be one of: ${validTypes.join(', ')}`,
      );
    }

    switch (rule.type) {
      case 'url-parameter':
        if (typeof rule.parameter !== 'string' || rule.parameter.trim() === '') {
          throw new ConfigValidationError(
            `extraction.${fieldName}.parameter is required for url-parameter type`,
          );
        }
        break;

      case 'attribute':
        if (typeof rule.attribute !== 'string' || rule.attribute.trim() === '') {
          throw new ConfigValidationError(
            `extraction.${fieldName}.attribute is required for attribute type`,
          );
        }
        if (typeof rule.selector !== 'string' || rule.selector.trim() === '') {
          throw new ConfigValidationError(
            `extraction.${fieldName}.selector is required for attribute type`,
          );
        }
        break;

      case 'element-text':
        if (typeof rule.selector !== 'string' || rule.selector.trim() === '') {
          throw new ConfigValidationError(
            `extraction.${fieldName}.selector is required for element-text type`,
          );
        }
        break;

      case 'regex':
        if (typeof rule.pattern !== 'string' || rule.pattern.trim() === '') {
          throw new ConfigValidationError(
            `extraction.${fieldName}.pattern is required for regex type`,
          );
        }
        // Validate regex pattern
        try {
          new RegExp(rule.pattern, rule.flags || '');
        } catch (e) {
          throw new ConfigValidationError(
            `extraction.${fieldName}.pattern is not a valid regular expression`,
          );
        }
        break;
    }

    // Validate transform if provided
    if (rule.transform !== undefined) {
      const validTransforms: TransformType[] = [
        'strip-utm',
        'strip-query',
        'decode-uri',
        'lowercase',
        'trim',
      ];
      const transforms = Array.isArray(rule.transform) ? rule.transform : [rule.transform];

      for (const transform of transforms) {
        if (!validTransforms.includes(transform)) {
          throw new ConfigValidationError(
            `extraction.${fieldName}.transform must be one of: ${validTransforms.join(', ')}`,
          );
        }
      }
    }

    if (rule.default !== undefined && typeof rule.default !== 'string') {
      throw new ConfigValidationError(`extraction.${fieldName}.default must be a string`);
    }
  }
}
